import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { applyKeywordSearchParam } from '../../util/listenerSearch';
import { catalogMatches, searchParamsFor } from '../../util/searchSuggest';

import css from './KeywordSearchBar.module.css';

const DEBOUNCE_MS = 450;

/**
 * Visible keyword field on Find a Listener. Writes `?keywords=` so results
 * filter by display name, bio, and topics alongside conversation types.
 *
 * @param {Object} props
 * @param {Object} [props.search]
 * @param {string} [props.pageName]
 * @param {string} [props.listingTypeParam]
 * @returns {JSX.Element}
 */
const KeywordSearchBar = props => {
  const { search = {}, pageName = 'SearchPage', listingTypeParam } = props;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const urlKeywords = typeof search.keywords === 'string' ? search.keywords : '';
  const [value, setValue] = useState(urlKeywords);
  const debounceRef = useRef(null);

  useEffect(() => {
    setValue(urlKeywords);
  }, [urlKeywords]);

  useEffect(() => {
    return () => {
      window.clearTimeout(debounceRef.current);
    };
  }, []);

  const commit = nextValue => {
    const next = applyKeywordSearchParam(search, nextValue);
    const current = applyKeywordSearchParam(search, urlKeywords);
    if (stringifyStable(next) === stringifyStable(current)) {
      return;
    }
    history.push(
      createResourceLocatorString(
        pageName,
        routeConfiguration,
        listingTypeParam ? { listingType: listingTypeParam } : {},
        next
      )
    );
  };

  const scheduleCommit = nextValue => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => commit(nextValue), DEBOUNCE_MS);
  };

  const onSubmit = event => {
    event.preventDefault();
    window.clearTimeout(debounceRef.current);
    commit(value);
  };

  const [focused, setFocused] = useState(false);
  const suggestions = focused ? catalogMatches(value, 6) : [];
  const pick = entry => {
    window.clearTimeout(debounceRef.current);
    setValue('');
    setFocused(false);
    const { keywords, page, topics, teaches, offer, ...rest } = search;
    history.push(
      createResourceLocatorString(
        pageName,
        routeConfiguration,
        listingTypeParam ? { listingType: listingTypeParam } : {},
        { ...rest, ...searchParamsFor(entry) }
      )
    );
  };

  const onClear = () => {
    window.clearTimeout(debounceRef.current);
    setValue('');
    commit('');
  };

  return (
    <form className={css.root} role="search" onSubmit={onSubmit}>
      <label className={css.label} htmlFor="listener-keyword-search">
        <FormattedMessage id="KeywordSearchBar.label" />
      </label>
      <div className={css.row}>
        <input
          id="listener-keyword-search"
          className={css.input}
          type="search"
          name="keywords"
          value={value}
          autoComplete="off"
          placeholder={intl.formatMessage({ id: 'KeywordSearchBar.placeholder' })}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          onChange={event => {
            const next = event.target.value;
            setValue(next);
            scheduleCommit(next);
          }}
        />
        <button className={css.submit} type="submit">
          <FormattedMessage id="KeywordSearchBar.submit" />
        </button>
        {urlKeywords || value ? (
          <button className={css.clear} type="button" onClick={onClear}>
            <FormattedMessage id="KeywordSearchBar.clear" />
          </button>
        ) : null}
      </div>
      {suggestions.length ? (
        <ul className={css.suggest} role="listbox" aria-label="Topics and skills" data-search-suggest="true">
          {suggestions.map(e => (
            <li key={`${e.kind}:${e.id}`} role="option" aria-selected="false">
              <button type="button" className={css.suggestItem} onMouseDown={ev => ev.preventDefault()} onClick={() => pick(e)}>
                <span className={css.suggestKind}>{e.kind === 'skill' ? `${e.emoji || '✨'} Learn` : e.kind === 'interest' ? '💬 Interest' : '💬 Talk'}</span>
                {e.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
};

const stringifyStable = params =>
  Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');

export default KeywordSearchBar;
